import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ModelSelectorModal from "../components/ModelSelectorModal";
import SearchHeroMobile from "../components/home/SearchHeroMobile";
import { trackEvent } from "../utils/analytics";
import { useVehicle } from "../context/VehicleContext";
import { categories } from "../data/homeData";
import harnessImage from "../assets/images/harness.png";
import fluidsImg from "../assets/images/CATEGTuneup.PNG";
import detailImg from "../assets/images/CATEGDetail.PNG";
import partsImg from "../assets/images/PWCPARTS.PNG";
import accsImg from "../assets/images/ACCSPWC.PNG";
import spraysImg from "../assets/images/SPRAYSPWC.PNG";


import { fitmentData } from "../data/fitmentData";

const makes = Object.keys(fitmentData);
const models = Object.fromEntries(
  Object.entries(fitmentData).map(([make, yearMap]) => [
    make,
    [...new Set(Object.values(yearMap).flat())]
  ])
);
const years = [...new Set(
  Object.values(fitmentData).flatMap(yearMap => Object.keys(yearMap).map(Number))
)].sort((a, b) => b - a);

/* ── Compact Product Card ── */
function MobileProductCard({ item }) {
 const navigate = useNavigate();
 const today = new Date();
 const start = new Date(today); start.setDate(today.getDate() + 3);
 const end = new Date(today); end.setDate(today.getDate() + 4);
 const deliveryStr = `${start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
 const fastStr = start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

 const displayPrice = item.variants?.length
   ? Math.min(...item.variants.map(v => v.price))
   : item.price || 0;
 const main = Math.floor(displayPrice);
 const cents = (displayPrice % 1).toFixed(2).substring(2);

 return (
   <div onClick={() => navigate(`/product/${item.listingId}`)} style={{
     display: "flex", gap: 14, padding: 14, borderRadius: 20,
     background: "linear-gradient(180deg,#ffffff 0%,#f4f6ff 100%)",
     border: "2px solid #fff",
     boxShadow: "0 0 20px rgba(120,170,255,0.12), 0 10px 24px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,1)",
     cursor: "pointer", alignItems: "center", position: "relative"
   }}>
     {/* Stock badge */}
     <div style={{
       position: "absolute", top: 10, left: 10,
       fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
       background: "rgba(90,142,252,0.1)", border: "1px solid rgba(90,142,252,0.2)", color: "#3157b7"
     }}>
       {(item.unitsAvailable ?? 1) > 0 ? "In Stock" : "Out of Stock"}
     </div>

     {/* Image */}
   <div style={{
  width: 110, height: 110, flexShrink: 0, display: "flex", alignItems: "center",
  justifyContent: "center", marginTop: 8
}}>
  <img
  src={
  item.variants?.find(v => v.condition === "used")?.images?.[0] ||
  item.variants?.find(v => v.condition === "new")?.images?.[0] ||
  item.images?.[0] ||
  harnessImage
}
    alt={item.title}
    style={{ width: "100%", height: "100%", objectFit: "contain" }}
  />
</div>

     {/* Info */}
     <div style={{ flex: 1, minWidth: 0 }}>
       <div style={{
         fontSize: 13, lineHeight: "18px", color: "#1d1d1f", marginBottom: 6,
         fontWeight: 500, overflow: "hidden", display: "-webkit-box",
         WebkitLineClamp: 2, WebkitBoxOrient: "vertical"
       }}>
         {item.title}
       </div>
       <div style={{ display: "flex", alignItems: "baseline", marginBottom: 5 }}>
         <span style={{ fontSize: 13, color: "#111" }}>$</span>
         <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.8px", color: "#111", lineHeight: 1 }}>{main}</span>
         <span style={{ fontSize: 13, color: "#111" }}>{cents}</span>
       </div>
       <div style={{ fontSize: 11, color: "#1d1d1f", marginBottom: 2 }}>
         FREE delivery <b>{item.delivery || deliveryStr}</b>
       </div>
       <div style={{ fontSize: 11, color: "#565959", marginBottom: 10 }}>
         ⚡ Fastest <b>{item.fastest || fastStr}</b>
       </div>
       <button
         onClick={e => { e.stopPropagation(); navigate(`/product/${item.listingId}`); }}
         style={{
           width: "100%", padding: "9px 0", borderRadius: 999, border: "none",
           background: "linear-gradient(180deg,#5a8efc,#1f57c8)", color: "#fff",
           fontSize: 13, fontWeight: 700, cursor: "pointer",
           boxShadow: "0 6px 14px rgba(62,111,227,0.3)"
         }}
       >
         Buy Now
       </button>
     </div>
   </div>
 );
}

/* ── Section Header ── */
function SectionHeader({ title, onSeeAll }) {
 return (
   <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
     <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "-0.5px", color: "#0f0f17" }}>{title}</h2>
     {onSeeAll && (
       <button onClick={onSeeAll} style={{ background: "none", border: "none", fontSize: 13, fontWeight: 600, color: "#2D6CFF", cursor: "pointer", padding: 0 }}>
         See all →
       </button>
     )}
   </div>
 );
}

/* ── Main ── */
export default function HomeScreenMobile() {
 const navigate = useNavigate();
 const [searchQuery, setSearchQuery] = useState("");
 const [listings, setListings] = useState([]);
 const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
 const [selectedYear, setSelectedYear] = useState(null);
 const [selectedMake, setSelectedMake] = useState(null);
 const [selectedModel, setSelectedModel] = useState(null);
 const [selectedSystem, setSelectedSystem] = useState(null);
 const [selectedSubSystem, setSelectedSubSystem] = useState(null);
 const { selectedVehicle, vehicleLoaded, setSelectedVehicle } = useVehicle();

 useEffect(() => { trackEvent("site_visit"); }, []);

 useEffect(() => {
   if (!vehicleLoaded) return;
   let url = "https://eleven11-i6v4.onrender.com/api/listings";
   if (selectedVehicle) {
     const params = new URLSearchParams({
       year: selectedVehicle.year,
       manufacturer: selectedVehicle.manufacturer,
       model: selectedVehicle.model
     });
     url += `?${params.toString()}`;
   }
   fetch(url)
     .then(r => r.json())
     .then(data => setListings([...data].sort(() => Math.random() - 0.5)))
     .catch(console.error);
 }, [selectedVehicle, vehicleLoaded]);

 const usedOEM = useMemo(() => listings.filter(l => l.condition === "used").slice(0, 4), [listings]);
 const newOEM = useMemo(() => listings.filter(l => l.condition === "new").slice(0, 4), [listings]);
 const sprays = useMemo(() => listings.filter(l => l.category === "Lubricants & Sprays").slice(0, 4), [listings]);

 const handleSearch = useCallback((q) => {
   const final = (q || searchQuery).trim();
   if (!final) return;
   let url = `/search?q=${encodeURIComponent(final)}`;
   if (selectedVehicle) {
     const p = new URLSearchParams({
       manufacturer: selectedVehicle.manufacturer,
       model: selectedVehicle.model,
       year: selectedVehicle.year
     });
     url += `&${p.toString()}`;
   }
   navigate(url);
 }, [searchQuery, selectedVehicle, navigate]);

 const handleClearVehicle = useCallback(e => {
   e.stopPropagation();
   setSelectedVehicle(null);
   localStorage.removeItem("selectedVehicle");
 }, [setSelectedVehicle]);

const featuredCategories = [
  {
    label: "Used OEM", path: "/category/used-oem",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
  },
  {
    label: "Accessories", path: "/category/accessories",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  },
  {
    label: "Detailing", path: "/category/marine-detailing",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z"/></svg>
  },
  {
    label: "Lubricants", path: "/category/lubricants",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
  },
  {
    label: "Fluids", path: "/category/fluids",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z"/></svg>
  },
  {
    label: "Parts", path: "/category/new-oem",
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/><path d="M12 2v2m0 16v2M2 12h2m16 0h2"/></svg>
  },
];


 return (
   <div style={{ background: "#F6F7FB", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, Inter, system-ui", overflowX: "hidden", paddingBottom: 60 }}>

    {/* ── HERO ── */}
<SearchHeroMobile
  searchQuery={searchQuery}
  setSearchQuery={setSearchQuery}
  onSearch={handleSearch}
/>

{/* Vehicle Selector */}
<div style={{ padding: "0 16px 20px" }}>
  <button
    onClick={() => setModelSelectorOpen(true)}
    style={{
      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "13px 16px", borderRadius: 16, border: "1.5px solid rgba(0,0,0,0.07)",
      background: selectedVehicle ? "linear-gradient(135deg,#f0f4ff,#e8edff)" : "rgba(255,255,255,0.9)",
      cursor: "pointer", boxSizing: "border-box"
    }}
  >
    <div style={{ textAlign: "left" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: selectedVehicle ? "#2D6CFF" : "#111827" }}>
        {selectedVehicle
          ? `${selectedVehicle.year} ${selectedVehicle.manufacturer} ${selectedVehicle.model}`
          : "Select Year / Make / Model"}
      </div>
      <div style={{ fontSize: 11, color: selectedVehicle ? "#6B9BFF" : "#9CA3AF", marginTop: 2 }}>
        {selectedVehicle ? "✓ Showing parts for your ski" : "Find parts that fit your ski"}
      </div>
    </div>
    {selectedVehicle ? (
      <div onClick={handleClearVehicle} style={{ width: 26, height: 26, borderRadius: 999, background: "rgba(45,108,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#2D6CFF" }}>×</div>
    ) : (
      <div style={{ fontSize: 16, color: "#9CA3AF" }}>→</div>
    )}
  </button>
</div>
     {/* ── CATEGORY PILLS ── */}
     <div style={{ padding: "0 16px", marginBottom: 24 }}>


       <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
         {featuredCategories.map(cat => (
           <button
             key={cat.label}
             onClick={() => navigate(cat.path)}
             style={{
               flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
               padding: "12px 16px", borderRadius: 16, border: "1.5px solid rgba(0,0,0,0.07)",
               background: "#fff", cursor: "pointer", minWidth: 72,
               boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
             }}
           >
           {cat.icon}

             <span style={{ fontSize: 11, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>{cat.label}</span>
           </button>
         ))}
       </div>
         </div>

     {/* ── USED OEM PARTS ── */}
     {usedOEM.length > 0 && (
       <div style={{ padding: "0 16px", marginBottom: 28 }}>
         <SectionHeader title="Used OEM Parts" onSeeAll={() => navigate("/category/used-oem")} />
         <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
           {usedOEM.slice(0, 3).map(item => <MobileProductCard key={item._id} item={item} />)}
         </div>
       </div>
     )}

     {/* ── PROMO BANNER ── */}

     <div style={{
       margin: "0 16px 28px", borderRadius: 20,
       background: "linear-gradient(135deg,#0f0f17,#1e2340)",
       padding: "24px 20px", position: "relative", overflow: "hidden"
     }}>
       <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(90,142,252,0.15)" }} />
       <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: "#5a8efc", marginBottom: 8 }}>ELEVEN GUARANTEE</div>
       <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px", marginBottom: 6 }}>OEM Quality, Direct Price</div>
       <div style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.6, marginBottom: 16 }}>
         Every part is verified, tested, and ready to install on your watercraft.
       </div>
       <button
         onClick={() => navigate("/search?q=oem")}
         style={{ padding: "10px 20px", borderRadius: 999, border: "none", background: "#5a8efc", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
       >
         Shop All Parts →
       </button>
     </div>

     {/* ── LUBRICANTS ── */}
     {sprays.length > 0 && (
       <div style={{ padding: "0 16px", marginBottom: 28 }}>
         <SectionHeader title="Lubricants & Sprays" onSeeAll={() => navigate("/category/lubricants")} />
         <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
           {sprays.map(item => {
             const price = item.variants?.length ? Math.min(...item.variants.map(v => v.price)) : item.price || 0;
             return (
               <div
                 key={item._id}
                 onClick={() => navigate(`/product/${item.listingId}`)}
                 style={{
                   flexShrink: 0, width: 150, borderRadius: 18,
                   background: "linear-gradient(180deg,#fff 0%,#f4f6ff 100%)",
                   border: "2px solid #fff", boxShadow: "0 8px 20px rgba(0,0,0,0.07)",
                   cursor: "pointer", overflow: "hidden"
                 }}
               >
               <div style={{ height: 140, background: "#f6f7f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
  <img src={
  item.variants?.find(v => v.condition === "used")?.images?.[0] ||
  item.variants?.find(v => v.condition === "new")?.images?.[0] ||
  item.images?.[0] ||
  harnessImage
}

alt={item.title} style={{ width: "95%", height: "95%", objectFit: "contain" }} />
</div>

                 <div style={{ padding: "10px 12px 12px" }}>
                   <div style={{ fontSize: 12, color: "#1d1d1f", marginBottom: 6, fontWeight: 500, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: "16px", minHeight: 32 }}>
                     {item.title}
                   </div>
                   <div style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>${Number(price).toFixed(2)}</div>
                 </div>
               </div>
             );
           })}
         </div>
       </div>
     )}

     {/* ── NEW OEM PARTS ── */}
     {newOEM.length > 0 && (
       <div style={{ padding: "0 16px", marginBottom: 28 }}>
         <SectionHeader title="Brand New OEM Parts" onSeeAll={() => navigate("/category/new-oem")} />
         <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
           {newOEM.slice(0, 3).map(item => <MobileProductCard key={item._id} item={item} />)}
         </div>
       </div>
     )}

     {/* ── TRUST BAR ── */}
     <div style={{ margin: "0 16px 28px", display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch", paddingBottom: 4 }}>
 {[
  {
    text: "OEM Verified Parts",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
  },
  {
    text: "Fast Global Shipping",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
  },
  {
    text: "Fully Tested",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11m0 0H5a2 2 0 01-2-2V9m6 5h10a2 2 0 002-2V9m0 0H3"/></svg>
  },
  {
    text: "Expert Support",
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
  },
].map(t => (
  <div key={t.text} style={{
    flexShrink: 0, display: "flex", alignItems: "center", gap: 8,
    padding: "10px 14px", borderRadius: 14, background: "#fff",
    border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
  }}>
    {t.icon}
    <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>{t.text}</span>
  </div>
))}
     </div>

{/* ── FEATURED CATEGORIES ── */}
<div style={{ padding: "0 16px", marginBottom: 28 }}>
  <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 800, letterSpacing: "-0.5px", color: "#0f0f17" }}>
    Featured Categories
  </h2>
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
    {[
     { name: "Parts", slug: "used-oem", image: partsImg },
{ name: "Accessories", slug: "accessories", image: accsImg },
{ name: "Marine Detailing", slug: "marine-detailing", image: detailImg },
{ name: "Lubricants & Sprays", slug: "lubricants", image: spraysImg },

    ].map((cat, i) => (
      <div
        key={i}
        onClick={() => navigate(`/category/${cat.slug}`)}
        style={{ borderRadius: 20, background: "#EEEEEE", aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.08)", position: "relative" }}
      >
        <img src={cat.image} alt={cat.name} style={{ width: "75%", height: "75%", objectFit: "contain" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "10px 12px", background: "linear-gradient(0deg, rgba(0,0,0,0.18), transparent)", borderRadius: "0 0 20px 20px" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{cat.name}</span>
        </div>
      </div>
    ))}
  </div>
  {/* Fluids - full width */}
  <div
    onClick={() => navigate("/category/fluids")}
    style={{ marginTop: 12, borderRadius: 20, background: "#EEEEEE", height: 120, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.08)", padding: "0 24px", position: "relative" }}
  >
    <span style={{ fontSize: 16, fontWeight: 700, color: "#222", zIndex: 1 }}>Fluids & Maintenance</span>
 <img src={fluidsImg} alt="Fluids" style={{ height: "90%", objectFit: "contain" }} />

  </div>
</div>



   {/* ── BRAND SECTION ── */}
<div style={{
  margin: "0 16px 28px", borderRadius: 20,
  background: "linear-gradient(145deg,#0f0f17,#16172a)",
  padding: "28px 22px", overflow: "hidden", position: "relative"
}}>
  {/* Background glow */}
  <div style={{ position: "absolute", bottom: -30, right: -30, width: 160, height: 160, borderRadius: "50%", background: "rgba(90,142,252,0.08)", pointerEvents: "none" }} />

  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#5a8efc", marginBottom: 10 }}>ELEVEN</div>
  <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.8px", color: "#fff", marginBottom: 6, lineHeight: 1.2 }}>
    OEM marine distribution, reimagined.
  </div>
  <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 24, lineHeight: 1.6 }}>
    Yamaha, Sea-Doo, Kawasaki and more — all in one place.
  </div>

  {/* Brand list — clean text style */}
  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
    {["YAMAHA", "SEA-DOO", "KAWASAKI", "NGK", "AMSOIL"].map((brand, i, arr) => (
      <div key={brand} style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        paddingTop: 12, paddingBottom: 12,
        borderBottom: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none"
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2, color: "rgba(255,255,255,0.75)" }}>
          {brand}
        </span>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>→</span>
      </div>
    ))}
  </div>
</div>


     {/* ── CONTACT CARD ── */}
     <div
       onClick={() => navigate("/contact")}
       style={{
         margin: "0 16px 28px", borderRadius: 20,
         background: "linear-gradient(145deg,rgba(255,255,255,0.96),rgba(245,245,247,0.92))",
         border: "1px solid rgba(255,255,255,0.95)",
         boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
         padding: "22px 20px", cursor: "pointer"
       }}
     >
       <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: "#A1A1AA", marginBottom: 10 }}>GET IN TOUCH</div>
       <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: "-1px", color: "#111", marginBottom: 8 }}>Contact Us</div>
       <div style={{ fontSize: 13, color: "#6E6E73", lineHeight: 1.6, marginBottom: 20 }}>
         Speak with our parts, products, logistics, or dealer support team.
       </div>
       <div style={{ display: "flex", justifyContent: "flex-end" }}>
         <div style={{ width: 44, height: 44, borderRadius: 999, background: "#111", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700 }}>→</div>
       </div>
     </div>

     {/* ── FOOTER ── */}
     <footer style={{ padding: "20px 16px 40px", textAlign: "center" }}>
       <div style={{ fontSize: 12, color: "#A1A1AA", marginBottom: 12 }}>© 2026 ELEVEN Industries</div>
       <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
    {[
  { label: "Privacy", path: "/privacy" },
  { label: "Terms", path: "/terms" },
  { label: "Contact", path: "/contact" },
  { label: "Wholesale", path: "/contact" },
].map(link => (
  <span key={link.label} onClick={() => navigate(link.path)} style={{ fontSize: 12, color: "#8E8E93", cursor: "pointer" }}>{link.label}</span>
))}

       </div>
     </footer>

     {/* Model Selector Modal */}
     <ModelSelectorModal
       diagramMode={false}
       modelSelectorOpen={modelSelectorOpen}
       setModelSelectorOpen={setModelSelectorOpen}
       selectedYear={selectedYear} setSelectedYear={setSelectedYear}
       selectedMake={selectedMake} setSelectedMake={setSelectedMake}
       selectedModel={selectedModel} setSelectedModel={setSelectedModel}
       selectedSystem={selectedSystem} setSelectedSystem={setSelectedSystem}
       selectedSubSystem={selectedSubSystem} setSelectedSubSystem={setSelectedSubSystem}
       makes={makes} models={models} years={years}
     />
   </div>
 );
}
